// Site-wide accessibility fixes for markup injected by third-party apps.

const strings = (() => {
  try {
    return JSON.parse(document.getElementById(`nss-a11y-strings`)?.textContent || `{}`);
  } catch (err) {
    return {};
  }
})();

// Klaviyo Reviews star widgets carry the rating only in an aria-label ("4.9 stars, ratings").
// On a <div> screen readers ignore it, so only "200 reviews" is read; on the PDP link it replaces
// the review count. Rewrite each widget once Klaviyo renders it so rating and count are read together.
const RATING_WIDGET = `.kl_reviews__star_rating_widget[aria-label]:not([data-nss-rating])`;
const ratingTemplate = strings.rating || `Rated [rating] out of 5 stars`;

function enhanceRatingWidget(widget) {
  const match = (widget.getAttribute(`aria-label`) || ``).match(/([\d.]+)\s*star/i);
  if (!match) return;

  const rating = ratingTemplate.replace(`[rating]`, match[1]);
  const count = widget.querySelector(`.kl_reviews__star_rating_widget__label`)?.textContent.trim();
  widget.setAttribute(`data-nss-rating`, match[1]);

  if (widget.tagName === `A`) {
    widget.setAttribute(`aria-label`, count ? `${rating}, ${count}` : rating);
    return;
  }

  widget.removeAttribute(`aria-label`);
  // The product banner already prints a visible "Rated X/5 Stars" prefix next to the widget.
  if (widget.closest(`.nss-product-banner__rating`)) return;

  const label = document.createElement(`span`);
  label.className = `nss-visually-hidden`;
  label.textContent = `${rating},`;
  widget.prepend(label);
}

// Scrollable text boxes (e.g. long review quotes capped with max-height) must be reachable by
// keyboard to be scrolled. Only boxes that actually overflow join the tab order.
const SCROLL_BOXES = `.nss-profile-card__quote`;

function syncScrollBoxes() {
  document.querySelectorAll(SCROLL_BOXES).forEach((box) => {
    const overflows = box.scrollHeight > box.clientHeight + 1;
    if (overflows && !box.hasAttribute(`tabindex`)) {
      box.setAttribute(`tabindex`, `0`);
      box.setAttribute(`data-nss-scroll-box`, ``);
    } else if (!overflows && box.hasAttribute(`data-nss-scroll-box`)) {
      box.removeAttribute(`tabindex`);
      box.removeAttribute(`data-nss-scroll-box`);
    }
  });
}

// Klaviyo sign-up popups trap focus while open but drop it on <body> when closed. Remember the
// last element focused outside the popup and return focus there on close.
let lastFocusOutsideKlaviyo = null;
document.addEventListener(`focusin`, (e) => {
  if (!e.target.closest(`.needsclick[role="dialog"], .klaviyo-form`) && e.target !== document.body) lastFocusOutsideKlaviyo = e.target;
});
const KLAVIYO_POPUP = `.needsclick[role="dialog"], .klaviyo-form`;
window.addEventListener(`klaviyoForms`, (e) => {
  if (e.detail?.type !== `close`) return;
  // "close" fires while focus is still inside the popup; Klaviyo removes it (and drops focus on
  // <body>) a moment later, so check again after the close animation.
  const restore = () => {
    const active = document.activeElement;
    const focusLost = !active || active === document.body || !active.isConnected || !!active.closest(KLAVIYO_POPUP);
    const target = lastFocusOutsideKlaviyo;
    if (focusLost && target && target.isConnected) target.focus({ preventScroll: true });
  };
  [50, 400, 1000].forEach((ms) => setTimeout(restore, ms));
});

// Rebuy Smart Cart toggles aria-hidden on its drawer but leaves its links and buttons in the tab
// order while closed. Mirror aria-hidden="true" as inert so keyboard users skip the hidden drawer.
const watchedCarts = new WeakSet();
const syncCartInert = (cart) => cart.toggleAttribute(`inert`, cart.getAttribute(`aria-hidden`) === `true`);

function watchRebuyCart() {
  document.querySelectorAll(`.rebuy-cart[aria-hidden]`).forEach((cart) => {
    syncCartInert(cart);
    if (watchedCarts.has(cart)) return;
    watchedCarts.add(cart);
    new MutationObserver(() => syncCartInert(cart)).observe(cart, { attributes: true, attributeFilter: [`aria-hidden`] });
  });
}

// Rebuy's bundle template (edited in the Rebuy app, not in this theme) puts a role on the bundle title
// link that links may not carry. A real link with an href is already exposed correctly without it.
const LINK_ROLES = [`button`, `checkbox`, `menuitem`, `menuitemcheckbox`, `menuitemradio`, `option`, `radio`, `switch`, `tab`, `treeitem`];
function fixRebuyBundleTitles() {
  document.querySelectorAll(`.rebuy-cart a.nss-rebuy-bundle__title[href][role]`).forEach((link) => {
    if (!LINK_ROLES.includes(link.getAttribute(`role`))) link.removeAttribute(`role`);
  });
}

let queued = false;
const run = () => {
  queued = false;
  document.querySelectorAll(RATING_WIDGET).forEach(enhanceRatingWidget);
  syncScrollBoxes();
  watchRebuyCart();
  fixRebuyBundleTitles();
};

new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(run);
}).observe(document.body, { childList: true, subtree: true });

// Overflow can change once web fonts and images finish loading.
window.addEventListener(`resize`, () => requestAnimationFrame(syncScrollBoxes));
window.addEventListener(`load`, syncScrollBoxes);
document.fonts?.ready.then(syncScrollBoxes);
run();
